import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

import en from "./locales/en.json";
import me from "./locales/me.json";
import it from "./locales/it.json";
import de from "./locales/de.json";
import tr from "./locales/tr.json";

export const LANGUAGES = [
  { code: "en", label: "English", native: "English", flag: "🇬🇧" },
  { code: "me", label: "Montenegrin", native: "Crnogorski", flag: "🇲🇪" },
  { code: "it", label: "Italian", native: "Italiano", flag: "🇮🇹" },
  { code: "de", label: "German", native: "Deutsch", flag: "🇩🇪" },
  { code: "tr", label: "Turkish", native: "Türkçe", flag: "🇹🇷" },
];

const STORAGE_KEY = "bl_lang";
const FIRST_VISIT_KEY = "bl_lang_first_visit";
const SUPPORTED = ["en", "me", "it", "de", "tr"];

// If user has never picked a language, prefer their browser language
// (falling back to English) and remember it as a "first-visit auto-pick".
function pickInitialLanguage() {
  const stored = typeof window !== "undefined" ? localStorage.getItem(STORAGE_KEY) : null;
  if (stored) return stored;
  const nav = (typeof navigator !== "undefined" && (navigator.languages?.[0] || navigator.language)) || "en";
  const short = nav.toLowerCase().split("-")[0];
  // Map common aliases to our supported codes
  const alias = { sr: "me", hr: "me", bs: "me", cnr: "me" };
  const code = alias[short] || short;
  const chosen = SUPPORTED.includes(code) ? code : "en";
  if (typeof window !== "undefined") {
    localStorage.setItem(STORAGE_KEY, chosen);
    // Only mark as first-visit auto-pick if it's non-English (i.e. we actually changed the UI)
    if (chosen !== "en") localStorage.setItem(FIRST_VISIT_KEY, chosen);
  }
  return chosen;
}

const initialLng = pickInitialLanguage();

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
      me: { translation: me },
      it: { translation: it },
      de: { translation: de },
      tr: { translation: tr },
    },
    lng: initialLng,
    fallbackLng: "en",
    supportedLngs: SUPPORTED,
    interpolation: { escapeValue: false },
    detection: {
      order: ["localStorage", "navigator"],
      lookupLocalStorage: STORAGE_KEY,
      caches: ["localStorage"],
    },
    returnEmptyString: false,
  });

if (typeof document !== "undefined") {
  document.documentElement.setAttribute("lang", initialLng);
}

export const setLanguage = (code) => {
  if (!code) return;
  localStorage.setItem(STORAGE_KEY, code);
  i18n.changeLanguage(code);
  document.documentElement.setAttribute("lang", code);
  // User has now made an explicit choice — clear the first-visit flag
  localStorage.removeItem(FIRST_VISIT_KEY);
};

/** Returns the auto-picked language code if this is the user's first visit
 *  and the browser landed them on a non-English language. Reads once and
 *  clears the flag so callers can show a welcome toast a single time. */
export const consumeFirstVisitLang = () => {
  if (typeof window === "undefined") return null;
  const code = localStorage.getItem(FIRST_VISIT_KEY);
  if (code) localStorage.removeItem(FIRST_VISIT_KEY);
  return code;
};

export default i18n;
